/**
 * [M4] Mail platform contracts — Lead scaffold ตาม tasks/M4-CONTRACTS.md §3 (freeze D-041)
 * - รูป wire = snake_case ตามแบบ contracts อื่น · filter conditions/actions ตาม §3.4 (ตายตัว)
 * - M4-02 เป็น owner หลัง scaffold merge — งานอื่นเสนอแก้ผ่าน PR Lead (hotspot §8)
 * - M4-11 เพิ่มไฟล์ใหม่ mail-contacts.ts (Contact, CalendarInviteCard) — ห้ามแก้ไฟล์นี้
 */
import { z } from "zod";

export const MailAccountStatus = z.enum(["active", "suspended", "offboarded"]);
export type MailAccountStatus = z.infer<typeof MailAccountStatus>;

export const MailFolder = z.enum(["inbox", "sent", "drafts", "archive", "trash", "spam"]);
export type MailFolder = z.infer<typeof MailFolder>;

/** [SA-04 · 0019] เพิ่ม "unknown" — ผลการส่งไม่แน่นอน: จริงใจกว่า failed/sent ปลอม และห้ามเป็นช่องทาง resend */
export const MailDeliveryStatus = z.enum(["queued", "sending", "sent", "delivered", "bounced", "failed", "canceled", "unknown"]);
export type MailDeliveryStatus = z.infer<typeof MailDeliveryStatus>;

/** ที่อยู่ผู้รับ/ผู้ส่งใน jsonb ของข้อความ */
export const MailEmailAddress = z.object({
  address: z.email(),
  name: z.string().nullable(),
});
export type MailEmailAddress = z.infer<typeof MailEmailAddress>;

export const MailAccount = z.object({
  id: z.uuid(),
  user_id: z.uuid(),
  primary_address_id: z.uuid().nullable(),
  display_name: z.string(),
  status: MailAccountStatus,
  quota_mb: z.number().int(),
  used_bytes: z.number().int(),
  send_limit_per_day: z.number().int(),
  send_suspended_until: z.iso.datetime().nullable(),
});
export type MailAccount = z.infer<typeof MailAccount>;

export const MailLabel = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string().nullable(),
});
export type MailLabel = z.infer<typeof MailLabel>;

export const MailThreadSummary = z.object({
  id: z.uuid(),
  subject: z.string().nullable(),
  last_at: z.iso.datetime(),
  message_count: z.number().int(),
  unread_count: z.number().int(),
  labels: z.array(z.string()),
  starred: z.boolean(),
  snoozed_until: z.iso.datetime().nullable(),
  muted: z.boolean(),
  /** การ์ดที่เกิดจาก convert (M4-12) — null = ยังไม่แปลง */
  ticket_id: z.uuid().nullable(),
  /** If-Match/ETag */
  version: z.number().int(),
});
export type MailThreadSummary = z.infer<typeof MailThreadSummary>;

export const MailMessage = z.object({
  id: z.uuid(),
  thread_id: z.uuid(),
  /** RFC Message-ID — null = ร่างยังไม่ส่ง */
  message_id: z.string().nullable(),
  in_reply_to: z.string().nullable(),
  from_address: z.string(),
  to: z.array(MailEmailAddress),
  cc: z.array(MailEmailAddress),
  bcc: z.array(MailEmailAddress),
  subject: z.string().nullable(),
  snippet: z.string(),
  /** body ใหญ่เกิน 5 MB ไม่เก็บในตาราง — อ่านผ่าน body_url (signed, R5) */
  body_url: z.string().nullable(),
  has_attachments: z.boolean(),
  size: z.number().int(),
  folder: MailFolder,
  read_at: z.iso.datetime().nullable(),
  flagged: z.boolean(),
  received_at: z.iso.datetime().nullable(),
  sent_at: z.iso.datetime().nullable(),
  delivery_status: MailDeliveryStatus.nullable(),
  spam_score: z.number().nullable(),
  /** external-sender banner */
  external: z.boolean(),
  version: z.number().int(),
});
export type MailMessage = z.infer<typeof MailMessage>;

export const MailThreadDetail = MailThreadSummary.extend({
  messages: z.array(MailMessage),
});
export type MailThreadDetail = z.infer<typeof MailThreadDetail>;

/** ลิงก์ .eml แบบ signed URL (อายุ 10 นาที) */
export const MailMessageRaw = z.object({
  url: z.string(),
  expires_at: z.iso.datetime(),
});
export type MailMessageRaw = z.infer<typeof MailMessageRaw>;

export const MailDraft = z.object({
  id: z.uuid(),
  thread_id: z.uuid().nullable(),
  from_address: z.email(),
  to: z.array(MailEmailAddress),
  cc: z.array(MailEmailAddress),
  bcc: z.array(MailEmailAddress),
  subject: z.string().nullable(),
  body_html: z.string().nullable(),
  body_text: z.string().nullable(),
  attachment_ids: z.array(z.uuid()),
  reply_context: z
    .object({
      message_id: z.uuid(),
      type: z.enum(["reply", "reply_all", "forward"]),
    })
    .nullable(),
  updated_at: z.iso.datetime(),
});
export type MailDraft = z.infer<typeof MailDraft>;

/** [closeout R1/R2 · design §3] สถานะสุดท้ายของ "การกดส่ง" ตาม lifecycle จริง — แยกจาก delivery_status ของข้อความ */
export const MailSendStatus = z.enum(["sent", "cancel_requested", "cancel_window_passed", "conflict_recoverable"]);
export type MailSendStatus = z.infer<typeof MailSendStatus>;

/** ผลส่งเรียบร้อย: โยงกลับ outbox ผ่าน outbox_message_id (undo/NDR map delivery_status) */
export const MailSendResult = z.object({
  message_id: z.uuid(),
  thread_id: z.uuid(),
  outbox_message_id: z.string().nullable(),
  delivery_status: MailDeliveryStatus,
  /** กรอบเวลา undo (send แบบไม่ระบุ = null) */
  undo_until: z.iso.datetime().nullable(),
  /**
   * [closeout R1/R2 · design §3] ผลของการส่งรอบนี้ตาม send cycle จริง:
   * - sent = submit เสร็จปกติ · cancel_requested = ถูกขอยกเลิกและ intent ถูกยกเลิกจริง (delivery=canceled)
   * - cancel_window_passed = มีการขอยกเลิกแต่เลยหน้าต่าง — อีเมลกำลัง/ได้ส่งไปแล้ว
   * - conflict_recoverable = ผลกู้คืนจากรอบก่อนที่ยังไม่แน่นอน — อ่านความจริงจากแถวข้อความ
   */
  send_status: MailSendStatus,
});
export type MailSendResult = z.infer<typeof MailSendResult>;

/** [closeout R2 · design §1 Cancel/unsend] ผลของ unsend/cancel-scheduled — canceled เฉพาะชนะจริง */
export const MailUnsendResult = z.object({
  id: z.uuid(),
  /** canceled = ยกเลิกสำเร็จจริง (ไม่มีอีเมลรอส่ง) · cancel_requested = กำลังส่งอยู่ — รอผล (writer/sweeper จบให้) */
  delivery_status: z.enum(["canceled", "cancel_requested"]),
});
export type MailUnsendResult = z.infer<typeof MailUnsendResult>;

// ---------------------------------------------------------------------------
// MailFilter — รูป jsonb conditions/actions ตาม §3.4 (ตายตัว)
// ---------------------------------------------------------------------------

export const MailFilterCondition = z.discriminatedUnion("field", [
  z.object({
    field: z.enum(["from", "to", "cc", "subject", "body", "header", "list_id", "attachment_name"]),
    match: z.enum(["contains", "equals", "regex", "starts", "ends"]),
    value: z.string(),
    not: z.boolean().optional(),
  }),
  z.object({
    field: z.literal("size"),
    op: z.enum(["gt", "lt"]),
    value: z.number(),
  }),
  z.object({
    field: z.enum(["has_attachment", "is_external", "spam_score_gte", "label"]),
    value: z.union([z.string(), z.number(), z.boolean()]),
  }),
  z.object({
    field: z.literal("received_at"),
    op: z.enum(["before", "after"]),
    /** ISO 8601 */
    value: z.string(),
  }),
  z.object({
    /** 0=อาทิตย์ · เวลาท้องถิ่น Asia/Bangkok */
    field: z.literal("time_window"),
    days: z.array(z.number().int().min(0).max(6)),
    minutes_from: z.number().int(),
    minutes_to: z.number().int(),
  }),
]);
export type MailFilterCondition = z.infer<typeof MailFilterCondition>;

export const MailFilterConditions = z.object({
  op: z.enum(["and", "or"]),
  items: z.array(MailFilterCondition),
});
export type MailFilterConditions = z.infer<typeof MailFilterConditions>;

export const MailFilterAction = z.discriminatedUnion("type", [
  z.object({ type: z.literal("add_label"), name: z.string() }),
  z.object({ type: z.literal("remove_label"), name: z.string() }),
  z.object({ type: z.literal("mark_read") }),
  z.object({ type: z.literal("star") }),
  z.object({ type: z.literal("mark_important") }),
  z.object({ type: z.literal("archive") }),
  z.object({ type: z.literal("trash") }),
  /** doc18 §6 move-to-quarantine — semantics ตาม §3.4 ท้าย (ใช้กลไก M4-07 ทั้งหมด) */
  z.object({ type: z.literal("quarantine") }),
  z.object({ type: z.literal("snooze"), hours: z.number() }),
  /** ภายในโดเมนที่ระบบจัดการเท่านั้น — D-013(ง) */
  z.object({ type: z.literal("forward_internal"), address: z.email() }),
  z.object({ type: z.literal("auto_reply_template"), template_id: z.string() }),
  z.object({ type: z.literal("convert_to_ticket"), service_code: z.string(), team_code: z.string() }),
  z.object({ type: z.literal("notify") }),
  z.object({ type: z.literal("stop") }),
]);
export type MailFilterAction = z.infer<typeof MailFilterAction>;

export const MailFilter = z.object({
  id: z.uuid(),
  /** null = domain-forced (admin) */
  account_id: z.uuid().nullable(),
  domain: z.string().nullable(),
  ord: z.number().int(),
  conditions: MailFilterConditions,
  actions: z.array(MailFilterAction),
  enabled: z.boolean(),
});
export type MailFilter = z.infer<typeof MailFilter>;

// ---------------------------------------------------------------------------
// Sync/push (M4-08) · quarantine (M4-07)
// ---------------------------------------------------------------------------

const ChangeSet = z.object({
  created: z.array(z.string()),
  updated: z.array(z.string()),
  destroyed: z.array(z.string()),
});

/** GET /api/v1/mail/changes?since= — เกิน 30 วัน/ไม่รู้จัก since → 410 mail_resync_required */
export const MailChangesResponse = z.object({
  state: z.string(),
  has_more: z.boolean().optional(),
  threads: ChangeSet,
  messages: ChangeSet,
  labels: ChangeSet,
  filters: ChangeSet,
});
export type MailChangesResponse = z.infer<typeof MailChangesResponse>;

/** /ws/mail + push signal — ไม่มีเนื้อหาใน payload (D-013 ฉ) */
export const MailPushSignal = z.object({
  type: z.literal("mail.changed"),
  state: z.string(),
  unread_count: z.number().int(),
});
export type MailPushSignal = z.infer<typeof MailPushSignal>;

export const PushSubscriptionInput = z.object({
  platform: z.enum(["web", "fcm", "apns"]),
  token: z.string().min(1),
  device_id: z.string().min(1),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInput>;

/** รายการ subscription (ไม่มี token ทุกรูป) */
export const MailPushSubscription = z.object({
  id: z.uuid(),
  platform: z.enum(["web", "fcm", "apns"]),
  device_id: z.string(),
  created_at: z.iso.datetime(),
  last_seen_at: z.iso.datetime().nullable(),
});
export type MailPushSubscription = z.infer<typeof MailPushSubscription>;

/**
 * [SA-09 · #281] สถานะ push ของ deployment — release นี้ปิดทั้ง Web/native (`{enabled:false, reason:"push_disabled"}`)
 * POST สมัครใหม่ถูกปฏิเสธ 503 `service_unavailable` (details reason=push_disabled) · GET/DELETE ของเดิมยังใช้ได้
 */
export const MailPushCapability = z.object({
  enabled: z.boolean(),
  reason: z.string().nullable(),
});
export type MailPushCapability = z.infer<typeof MailPushCapability>;

export const MailPushSubscriptionList = z.object({
  items: z.array(MailPushSubscription),
  capability: MailPushCapability,
});
export type MailPushSubscriptionList = z.infer<typeof MailPushSubscriptionList>;

/** รายการใน /api/v1/mail/quarantine (เจ้าของเห็นของตัวเอง · admin เห็นทุกบัญชี — D-041 M4-4) */
export const QuarantineItem = z.object({
  id: z.uuid(),
  inbound_message_id: z.uuid(),
  account_id: z.uuid().nullable(),
  domain: z.string().nullable(),
  /** null = กักกันด้วย filter (reasons:["filter:<filter_id>"]) */
  score: z.number().nullable(),
  reasons: z.array(z.string()),
  received_at: z.iso.datetime().nullable(),
  digest_sent_at: z.iso.datetime().nullable(),
  released_at: z.iso.datetime().nullable(),
  expires_at: z.iso.datetime(),
  /** ตัวอย่างเนื้อหาพอสรุปรายการ (จาก inbound_messages) */
  from_address: z.string().nullable(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
});
export type QuarantineItem = z.infer<typeof QuarantineItem>;

// ---------------------------------------------------------------------------
// Settings (GET/PATCH /api/v1/mail/settings)
// ---------------------------------------------------------------------------

export const MailAutoReply = z.object({
  enabled: z.boolean(),
  from: z.iso.datetime(),
  until: z.iso.datetime(),
  subject: z.string(),
  body_external: z.string(),
  body_internal: z.string(),
});
export type MailAutoReply = z.infer<typeof MailAutoReply>;

export const MailSettings = z.object({
  display_name: z.string(),
  signature_html: z.string().nullable(),
  auto_reply: MailAutoReply.nullable(),
  prefs: z.record(z.string(), z.unknown()),
});
export type MailSettings = z.infer<typeof MailSettings>;

// ---------------------------------------------------------------------------
// [M4-01] mail-admin schemas — bounded hunk ผ่าน PR Lead (task M4-01 · M4-CONTRACTS §4)
// ---------------------------------------------------------------------------

export const MailAddressKind = z.enum(["account", "alias", "group", "shared_mailbox", "system", "catch_all"]);
export type MailAddressKind = z.infer<typeof MailAddressKind>;

export const MailSystemHandler = z.enum(["bounce", "notify", "csat", "dmarc", "tlsrpt", "unsub", "side", "reject", "postmaster", "abuse"]);
export type MailSystemHandler = z.infer<typeof MailSystemHandler>;

export const MailGrantRight = z.enum(["send_as", "send_on_behalf", "read", "manage"]);
export type MailGrantRight = z.infer<typeof MailGrantRight>;

/** สมาชิก group: ภายในเท่านั้น — external ถูก API ปฏิเสธ (422 external_member_forbidden · D-013 ง)
 *  ที่ zod ยอมรับ object ใด ๆ แล้ว service ตรวจ: ให้ error code เดียวกัน (external_member_forbidden) แทน "Invalid input" กลาง ๆ */
export const MailGroupMember = z.record(z.string(), z.unknown());
export type MailGroupMember = z.infer<typeof MailGroupMember>;

const addressCommon = {
  domain: z.string().min(3).max(253).regex(/^[a-z0-9.-]+$/i),
  // `*` ล้วน = catch_all ของโดเมน (M4-CONTRACTS §2) — นอกนั้นต้องมีอักขระจริงนำหน้า `*` ท้ายได้ 1 ตัว
  local_part: z.string().min(1).max(64).regex(/^([a-z0-9._-]+\*?|\*)$/i),
  enabled: z.boolean().optional(),
};

/** POST /api/v1/mail-admin/addresses — target ผูกกับ kind (รูปตาม M4-CONTRACTS §2 ตายตัว) */
export const EmailAddressInput = z.discriminatedUnion("kind", [
  z.object({ ...addressCommon, kind: z.literal("account"), account_id: z.uuid() }),
  z.object({ ...addressCommon, kind: z.literal("alias"), account_id: z.uuid().optional(), mailbox_id: z.uuid().optional() }),
  z.object({
    ...addressCommon,
    kind: z.literal("group"),
    members: z.array(MailGroupMember).min(1),
    send_as_policy: z.enum(["members", "admins"]).default("members"),
    reply_to_group: z.boolean().default(false),
  }),
  z.object({ ...addressCommon, kind: z.literal("shared_mailbox"), mailbox_id: z.uuid() }),
  z.object({ ...addressCommon, kind: z.literal("system"), handler: MailSystemHandler }),
  z.object({
    ...addressCommon,
    kind: z.literal("catch_all"),
    target: z.union([z.object({ type: z.literal("address_id"), address_id: z.uuid() }), z.object({ type: z.literal("reject") })]),
  }),
]);
export type EmailAddressInput = z.infer<typeof EmailAddressInput>;

/** PATCH /api/v1/mail-admin/addresses/:id — เปลี่ยน target ทั้งชุด (kind ต้องตรงแถวเดิม) / enabled */
export const EmailAddressPatch = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("account"), account_id: z.uuid(), enabled: z.boolean().optional() }),
  z.object({ kind: z.literal("alias"), account_id: z.uuid().optional(), mailbox_id: z.uuid().optional(), enabled: z.boolean().optional() }),
  z.object({
    kind: z.literal("group"),
    members: z.array(MailGroupMember).min(1),
    send_as_policy: z.enum(["members", "admins"]).default("members"),
    reply_to_group: z.boolean().default(false),
    enabled: z.boolean().optional(),
  }),
  z.object({ kind: z.literal("shared_mailbox"), mailbox_id: z.uuid(), enabled: z.boolean().optional() }),
  z.object({ kind: z.literal("system"), handler: MailSystemHandler, enabled: z.boolean().optional() }),
  z.object({
    kind: z.literal("catch_all"),
    target: z.union([z.object({ type: z.literal("address_id"), address_id: z.uuid() }), z.object({ type: z.literal("reject") })]),
    enabled: z.boolean().optional(),
  }),
]);
export type EmailAddressPatch = z.infer<typeof EmailAddressPatch>;

export const MailAddressAdmin = z.object({
  id: z.uuid(),
  domain: z.string(),
  local_part: z.string(),
  kind: MailAddressKind,
  target: z.record(z.string(), z.unknown()),
  enabled: z.boolean(),
  grants: z.array(
    z.object({
      id: z.uuid(),
      account_id: z.uuid(),
      rights: z.array(MailGrantRight),
    }),
  ),
});
export type MailAddressAdmin = z.infer<typeof MailAddressAdmin>;

/** POST /api/v1/mail-admin/addresses/:id/grants */
export const MailGrantInput = z.object({
  account_id: z.uuid(),
  rights: z.array(MailGrantRight).min(1),
});
export type MailGrantInput = z.infer<typeof MailGrantInput>;

/** POST /api/v1/mail-admin/accounts — provision ผูก user ที่เคย login หรือ staff-invite ล่วงหน้า (D-041 M4-6) */
export const MailAccountAdminInput = z.object({
  domain: z.string().min(3).max(253).regex(/^[a-z0-9.-]+$/i),
  local_part: z.string().min(1).max(64).regex(/^[a-z0-9._-]+$/i),
  display_name: z.string().min(1).max(200),
  quota_mb: z.number().int().min(1).max(1024000).optional(),
  send_limit_per_day: z.number().int().min(1).max(100000).optional(),
  user_id: z.uuid().optional(),
  invite: z
    .object({
      team_id: z.uuid(),
      role: z.enum(["team_lead", "member", "viewer"]),
    })
    .optional(),
});
export type MailAccountAdminInput = z.infer<typeof MailAccountAdminInput>;

/** PATCH /api/v1/mail-admin/accounts/:id — suspend/quota/limit แบบ conditional ตามสถานะที่คาด */
export const MailAccountAdminPatch = z.object({
  display_name: z.string().min(1).max(200).optional(),
  quota_mb: z.number().int().min(1).max(1024000).optional(),
  send_limit_per_day: z.number().int().min(1).max(100000).optional(),
  status: z.enum(["active", "suspended"]).optional(),
});
export type MailAccountAdminPatch = z.infer<typeof MailAccountAdminPatch>;

export const MailAccountAdmin = z.object({
  id: z.uuid(),
  user_id: z.uuid(),
  primary_address_id: z.uuid().nullable(),
  address: z.string().nullable(),
  display_name: z.string(),
  status: MailAccountStatus,
  quota_mb: z.number().int(),
  used_bytes: z.number().int(),
  send_limit_per_day: z.number().int(),
  send_suspended_until: z.iso.datetime().nullable(),
  /** true = สร้างล่วงหน้าผ่าน staff invite แล้วยังไม่เคย login (ผูก user_id แล้วรอ sso bind) */
  pending_bind: z.boolean(),
});
export type MailAccountAdmin = z.infer<typeof MailAccountAdmin>;

/** GET /api/v1/mail-admin/domains — wizard สถานะจากคอลัมน์ (DNS live = staging ของ owner) */
export const MailDomainAdminView = z.object({
  domain: z.string(),
  routing_enabled: z.boolean(),
  catch_all_target: z.record(z.string(), z.unknown()).nullable(),
  mta_sts_mode: z.enum(["none", "testing", "enforce"]),
  mta_sts_id: z.string().nullable(),
  tlsrpt_rua: z.string().nullable(),
  dmarc_rua: z.string().nullable(),
  max_inbound_mb: z.number().int(),
  default_quota_mb: z.number().int(),
  ses_identity_status: z.string().nullable(),
  ses_dkim_status: z.string().nullable(),
  cf_status: z.string().nullable(),
});
export type MailDomainAdminView = z.infer<typeof MailDomainAdminView>;

/** PATCH /api/v1/mail-admin/domains/:domain — M4-01 รับ catch_all_target เฉพาะ address_id|reject (quarantine รอ M4-04/07 fail-closed) */
export const MailDomainAdminPatch = z.object({
  routing_enabled: z.boolean().optional(),
  catch_all_target: z
    .union([z.object({ type: z.literal("address_id"), address_id: z.uuid() }), z.object({ type: z.literal("reject") })])
    .nullable()
    .optional(),
  mta_sts_mode: z.enum(["none", "testing", "enforce"]).optional(),
  mta_sts_id: z.string().max(128).nullable().optional(),
  tlsrpt_rua: z.string().max(320).nullable().optional(),
  dmarc_rua: z.string().max(320).nullable().optional(),
  max_inbound_mb: z.number().int().min(1).max(100).optional(),
  default_quota_mb: z.number().int().min(1).max(1024000).optional(),
});
export type MailDomainAdminPatch = z.infer<typeof MailDomainAdminPatch>;

/** POST /api/v1/mail-admin/domain-admins — role mail_admin ต่อโดเมน (doc18 §3.2) */
export const MailDomainAdminGrantInput = z.object({
  domain: z.string().min(3).max(253).regex(/^[a-z0-9.-]+$/i),
  user_id: z.uuid(),
});
export type MailDomainAdminGrantInput = z.infer<typeof MailDomainAdminGrantInput>;

// ---------------------------------------------------------------------------
// Mail core API (M4-02) — request/response ของ /api/v1/mail/*
// ---------------------------------------------------------------------------

/** [M4-02F] flag ที่เกี่ยวกับผู้เรียกที่ accounts/me เปิดเผยได้ — มีแต่ webmail (mail.webmail) ไม่เชลย flag อื่น */
export const MailAccountFlags = z.object({
  /** flag `mail.webmail` (M4-09/M4-10 เปิดหน้าเว็บ) — absent/off/error = false (fail-closed) */
  webmail: z.boolean(),
});
export type MailAccountFlags = z.infer<typeof MailAccountFlags>;

/**
 * [M4-02G] identity ที่ผู้เรียกส่งได้ (doc18 §4 "addresses ที่ส่งได้" — From picker ของ M4-10 R3)
 * = ที่อยู่ของบัญชีตัวเอง (email_addresses.target.account_id = ตัวเอง · row kind account|alias)
 * + ที่อยู่ที่ได้รับ grant send_as|send_on_behalf (address_grants) · enabled เท่านั้น —
 * สิทธิ์เดียวกับ resolveSendIdentity ของ send path: ไม่มีสิทธิ์จริง = ไม่ปรากฏ (no cross-account leak)
 */
export const MailIdentity = z.object({
  /** email_addresses.id — stable id ของ identity */
  id: z.uuid(),
  /** ที่อยู่เต็ม local@domain */
  address: z.string(),
  /** ชื่อแสดงใน From picker — เป็นชื่อของบัญชีตัวเองเท่านั้น (kind account|alias) · null (ที่อยู่ของบัญชีอื่น) = ใช้ address แสดงแทน */
  display: z.string().nullable(),
  /** account = ที่อยู่บัญชีตัวเอง (row kind=account) · alias = alias ที่ target กลับมาบัญชีตัวเอง · on_behalf = ที่อยู่ของบัญชีอื่นที่ได้รับ grant */
  kind: z.enum(["account", "alias", "on_behalf"]),
  /** ที่มาของสิทธิ์: account = ของบัญชีตัวเอง · send_as|send_on_behalf = จาก address_grants (มีทั้งคู่ → send_as ตามลำดับ resolveSendIdentity) */
  right: z.enum(["account", "send_as", "send_on_behalf"]),
});
export type MailIdentity = z.infer<typeof MailIdentity>;

/** [A1 · D-042] รายการบัญชีใน `accounts` ของ /mail/accounts/me — owned/shared พร้อมสิทธิ์และ From identities รายกล่อง */
export const MailAccountEntry = MailAccount.extend({
  access_role: z.enum(["owner", "shared"]).optional(),
  identities: z.array(MailIdentity).optional(),
  capabilities: z.object({ read: z.boolean(), manage: z.boolean(), send: z.boolean() }).optional(),
  state: z.string(),
  primary_address: z.string().nullable(),
});
export type MailAccountEntry = z.infer<typeof MailAccountEntry>;

/** GET /api/v1/mail/accounts/me — MailAccount + state ของ change feed + ที่อยู่หลัก */
export const MailAccountView = MailAccount.extend({
  /** state ปัจจุบันของ change feed (รูป "s<seq>-<b36 ts>" — เริ่มด้วย GET /mail/changes ของ M4-08) */
  state: z.string(),
  primary_address: z.string().nullable(),
  /** additive-optional [M4-02F]: สถานะ flag ของผู้เรียก (เฉพาะ webmail — fail-closed false) */
  flags: MailAccountFlags.optional(),
  /** additive-optional [M4-02G]: ที่อยู่ทั้งหมดที่ผู้เรียกส่งได้ (MailIdentity) — primary มาก่อน */
  identities: z.array(MailIdentity).optional(),
  /** additive-optional [A1 · D-042]: id ของบัญชีหลัก (earliest created — deterministic) · ฟิลด์ระดับบนสุดอื่น ๆ = primary */
  primary_account_id: z.uuid().optional(),
  /**
   * additive-optional [A1 · D-042]: บัญชี owned/shared ที่อ่านได้และ active — รายการเป็น MailAccountView
   * แบบไม่ซ้อน (ไม่มี accounts/flags ระดับรายการ) เพื่อไม่ทำ recursion ใน zod/openapi
   */
  accounts: z.array(MailAccountEntry).optional(),
});
export type MailAccountView = z.infer<typeof MailAccountView>;

/**
 * [A1 · D-042] account selector ของ account-scoped mail routes — additive-optional:
 * ไม่ส่ง = primary ของผู้เรียก · ส่ง = กล่องนั้น (เจ้าของ หรือ grant read สำหรับ read routes ·
 * message/label mutation = owner/manage · drafts/send = owner/send grant · settings = owner) — ไม่มีสิทธิ์ = 404
 */
export const MailAccountScopedQuery = z.object({ account_id: z.uuid().optional() });
export type MailAccountScopedQuery = z.infer<typeof MailAccountScopedQuery>;

export const MailSettingsPatch = z.object({
  display_name: z.string().min(1).max(200).optional(),
  signature_html: z.string().max(200_000).nullable().optional(),
  auto_reply: MailAutoReply.nullable().optional(),
  prefs: z.record(z.string(), z.unknown()).optional(),
});
export type MailSettingsPatch = z.infer<typeof MailSettingsPatch>;

export const MailLabelCreate = z.object({
  name: z.string().min(1).max(64),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
});
export type MailLabelCreate = z.infer<typeof MailLabelCreate>;

export const MailLabelPatch = z.object({
  name: z.string().min(1).max(64).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
});
export type MailLabelPatch = z.infer<typeof MailLabelPatch>;

/** label ที่ใช้ได้ใน threads list: folder ระบบ + pseudo-label starred/snoozed/unread + ชื่อ label ส่วนตัว */
export const MailThreadListQuery = z.object({
  label: z.string().min(1).max(64).optional(),
  q: z.string().max(512).optional(),
  cursor: z.string().max(512).optional(),
  /** กล่องที่ถูกแชร์ให้อ่าน (address_grants rights ∋ read) — เจ้าของไม่ต้องส่ง */
  account_id: z.uuid().optional(),
});
export type MailThreadListQuery = z.infer<typeof MailThreadListQuery>;

export const MailThreadList = z.object({
  items: z.array(MailThreadSummary),
  next_cursor: z.string().nullable(),
});
export type MailThreadList = z.infer<typeof MailThreadList>;

/** PATCH thread — ทุก field optional · ต้องส่ง If-Match (ETag = version) เสมอ ·
 *  strict [M4-02F]: unknown field = 422 ห้าม strip เงียบ · `archived` = ย้าย folder ของข้อความในเธรด
 *  ระดับเธรด (true: inbox→archive · false: archive→inbox — doc18 §4) */
export const MailThreadPatch = z.strictObject({
  add_labels: z.array(z.string().min(1).max(64)).max(50).optional(),
  remove_labels: z.array(z.string().min(1).max(64)).max(50).optional(),
  read: z.boolean().optional(),
  starred: z.boolean().optional(),
  snoozed_until: z.iso.datetime().nullable().optional(),
  muted: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type MailThreadPatch = z.infer<typeof MailThreadPatch>;

/** PATCH message — flags เท่านั้น */
export const MailMessagePatch = z.object({
  read: z.boolean().optional(),
  flagged: z.boolean().optional(),
});
export type MailMessagePatch = z.infer<typeof MailMessagePatch>;

export const MailMessageList = z.object({
  items: z.array(MailMessage),
  next_cursor: z.string().nullable(),
});
export type MailMessageList = z.infer<typeof MailMessageList>;

/** ไวยากรณ์ `from: to: subject: has:attachment label: before: after: is:unread larger:` + คำอิสระ (ILIKE ไทยได้) */
export const MailSearchQuery = z.object({
  q: z.string().min(1).max(512),
  cursor: z.string().max(512).optional(),
  account_id: z.uuid().optional(),
});
export type MailSearchQuery = z.infer<typeof MailSearchQuery>;

// ---------------------------------------------------------------------------
// Delegation grants (doc18 §4 · M4-CONTRACTS §4 Mail core)
// ---------------------------------------------------------------------------

/**
 * สิทธิ์ delegation ที่เจ้าของให้เองได้ (user-facing) — ⊆ MailGrantRight ของ mail-admin
 * send_as/manage เป็นของ mail-admin (M4-01) เท่านั้น · [B1 integration] ชื่อแยกจาก
 * MailGrantRight (enum เต็มของ M4-01) กัน duplicate identifier หลัง merge PR142
 */
export const MailDelegationRight = z.enum(["read", "send_on_behalf"]);
export type MailDelegationRight = z.infer<typeof MailDelegationRight>;

export const MailGrant = z.object({
  id: z.uuid(),
  /** address ที่ถูกแชร์ (default = primary address ของกล่อง) */
  address_id: z.uuid(),
  address: z.string(),
  /** account ของผู้ถูกแชร์ */
  grantee_account_id: z.uuid(),
  grantee_display_name: z.string(),
  rights: z.array(MailDelegationRight),
  created_at: z.iso.datetime(),
});
export type MailGrant = z.infer<typeof MailGrant>;

export const MailGrantCreate = z.object({
  grantee_account_id: z.uuid(),
  rights: z.array(MailDelegationRight).min(1).max(2),
  /** default = primary address ของกล่อง · ถ้าระบุต้องเป็น address ที่ชี้ target account_id กลับมาที่กล่องนี้ */
  address_id: z.uuid().optional(),
});
export type MailGrantCreate = z.infer<typeof MailGrantCreate>;

export const MailGrantList = z.object({
  items: z.array(MailGrant),
});
export type MailGrantList = z.infer<typeof MailGrantList>;
